import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryGrid from "@/pages/home/components/CategoryGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieGrid from "@/components/feature/MovieGrid";
import { movies, sortMovies, formatViews } from "@/lib/movies";
import { specials } from "@/mocks/movies";
import type { SpecialItem } from "@/types/movie";

const specialList = specials as unknown as SpecialItem[];

export default function Home() {
  const latest = sortMovies(movies, "latest").slice(0, 12);
  const hot = sortMovies(movies, "hot").slice(0, 8);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <HeroCarousel />

      {/* Search banner */}
      <div className="mt-6 flex flex-col items-start justify-between gap-4 rounded-xl bg-primary-600 px-6 py-5 md:flex-row md:items-center">
        <div>
          <h2 className="font-heading text-lg font-bold text-background-50 md:text-xl">
            想看什么，直接搜
          </h2>
          <p className="mt-1 text-sm text-background-100/80">
            覆盖电影、电视剧、综艺、动漫，收录数万部高清影视资源
          </p>
        </div>
        <Link
          to="/search"
          className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
        >
          <i className="ri-search-line text-lg" />
          进入搜索
        </Link>
      </div>

      {/* Categories */}
      <div className="mt-10">
        <SectionHeading title="影视分类" subtitle="四大频道，快速抵达想看的内容" icon="ri-apps-2-line" />
        <CategoryGrid />
      </div>

      {/* Latest */}
      <div className="mt-12">
        <SectionHeading
          title="最新影视"
          subtitle="持续更新，第一时间同步上线"
          icon="ri-time-line"
          moreTo="/category/电影"
        />
        <MovieGrid movies={latest} />
      </div>

      {/* Hot ranking */}
      <div className="mt-12">
        <SectionHeading
          title="热门影视榜"
          subtitle="根据播放热度实时排行的口碑佳作"
          icon="ri-fire-line"
          moreTo="/search"
          moreLabel="更多热门"
        />
        <div className="grid grid-cols-1 gap-3 rounded-xl border border-background-200 bg-background-50 p-4 md:grid-cols-2 md:p-5">
          {hot.map((movie, index) => (
            <Link
              key={movie.id}
              to={`/detail/${movie.id}`}
              className="group flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-background-100"
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm font-bold ${
                  index < 3
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-200 text-foreground-600"
                }`}
              >
                {index + 1}
              </span>
              <img
                src={movie.poster}
                alt={`${movie.title} 海报`}
                title={`${movie.title} 91免费看片`}
                className="h-14 w-10 shrink-0 rounded object-cover object-top"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground-950 group-hover:text-primary-700">
                  {movie.title}
                </p>
                <p className="mt-0.5 truncate text-xs text-foreground-500">
                  {movie.type} · {movie.year} · {movie.region}
                </p>
                <p className="mt-0.5 flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-fire-line text-accent-500" />
                  {formatViews(movie.views)}次播放
                </p>
              </div>
              <span className="shrink-0 text-base font-bold text-accent-600">
                {movie.rating.toFixed(1)}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* Specials */}
      <div className="mt-12">
        <SectionHeading
          title="影视专题推荐"
          subtitle="编辑精选主题合集，总有一款合你口味"
          icon="ri-bookmark-3-line"
          moreTo="/special"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {specialList.map((sp) => (
            <Link
              key={sp.id}
              to="/special"
              className="group relative overflow-hidden rounded-xl bg-primary-950"
            >
              <div className="relative h-44 w-full overflow-hidden">
                <img
                  src={sp.cover}
                  alt={`${sp.title} 专题封面`}
                  title={`${sp.title} 91免费看片`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/50 to-transparent" />
              </div>
              <div className="absolute inset-x-0 bottom-0 p-4">
                <h3 className="font-heading text-lg font-bold text-white">
                  {sp.title}
                </h3>
                <p className="mt-1 line-clamp-1 text-xs text-white/75">
                  {sp.reason}
                </p>
              </div>
              <span className="absolute right-4 top-4 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-semibold text-background-50">
                {sp.movieIds.length} 部
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}